import {
  AttributeType,
  IngestionContext,
  NormalizedIngestionSource,
} from '../types'
import {
  INoteNode,
  IQuestionNode,
  ISimplifiedNode,
  SimplifiedNodeWithChildren,
} from '../../../components/ingestion/IngestedTree'
import { attributeIngestion } from '../attributeIngestion'
import {
  createNodeList,
  createNoteNode,
  createShortResponse,
} from '../nodeCreation'
import { camelToTitle } from '../../../components/Helpers'
import { createUnorderedList, wrapB, wrapP, wrapSpan } from '../../html_utility'
import {
  createActivity,
  createAttachment,
  createAttachmentsList,
  createUnit,
} from '../../nodeCreationHelpers'

export function odellAttributeIngestion(
  newNode: SimplifiedNodeWithChildren,
  attr: AttributeType,
  sourceNode: NormalizedIngestionSource,
  context: IngestionContext,
) {
  if (attr.key === 'segments') {
    return doSegments(newNode, sourceNode, context)
  }
  if (attr.key === 'material' && sourceNode.type === 'oer:Lesson') {
    return doMaterials(newNode, sourceNode, context)
  }
  if (attr.key === 'kid:actMat') {
    return doKidActMap(newNode, sourceNode, context)
  }
  if (attr.key === 'kid:programGuidance') {
    return doKidProgramGuidance(newNode, sourceNode, context)
  }
  if (attr.key === 'kid:gDrive') {
    return doKidDrive(newNode, sourceNode, context)
  }
  attributeIngestion(newNode, attr, sourceNode, context)
}

function removeH5(htmlString: string) {
  const div = document.createElement('div')
  div.innerHTML = htmlString
  div.querySelector('h5')?.remove()
  return div.innerHTML
}

function doSegments(
  newNode: SimplifiedNodeWithChildren,
  sourceNode: NormalizedIngestionSource,
  context: IngestionContext,
) {
  const segments = sourceNode.segments as Array<{
    student: string | undefined
    teacher: string | undefined
  }>

  const { questions, teacherNotes } = segments.reduce<{
    questions: IQuestionNode[]
    teacherNotes: INoteNode[]
  }>(
    (acc, { student, teacher }) => {
      if (student) {
        const title = `Step ${acc.questions.length + 1}`
        acc.questions.push(
          createShortResponse(
            { title, description: removeH5(student) },
            context,
          ),
        )
      }
      if (teacher) {
        acc.teacherNotes.push(
          createNoteNode(
            {
              title: `Teacher Notes`,
              studentFacing: false,
              value: removeH5(teacher),
            },
            context,
          ),
        )
      }
      return acc
    },
    { questions: [], teacherNotes: [] },
  )

  newNode.nodes.push(...questions, ...teacherNotes)
}

function doMaterials(
  newNode: SimplifiedNodeWithChildren,
  sourceNode: NormalizedIngestionSource,
  context: IngestionContext,
) {
  doLessonActivityMaterials(newNode, sourceNode, context)
  doLessonMaterials(newNode, sourceNode, context)
}

function doAssociatedMaterials(material: any[]) {
  return material.reduce<string[]>((acc, m) => {
    if (m.type === 'ocx:AssociatedMaterial') {
      const partKeys = ['name', 'publisher', 'dateCreated']
      const parts = partKeys.reduce<string[]>((acc, key) => {
        const val = m[key]
        if (!val) return acc
        return [...acc, String(val)]
      }, [])
      acc.push(parts.join(', '))
    }
    return acc
  }, [])
}

function doLessonActivityMaterials(
  newNode: SimplifiedNodeWithChildren,
  sourceNode: NormalizedIngestionSource,
  context: IngestionContext,
) {
  const { grouped } = sourceNode.children!.reduce<{
    grouped: Record<string, string[]>
    seen: string[]
  }>(
    (acc, child) => {
      const { grouped, seen } = acc
      const material = child.material
      if (child.type !== 'oer:Activity' || !Array.isArray(material)) {
        return acc
      }
      const materials = doAssociatedMaterials(material).filter(
        (m) => !seen.includes(m),
      )
      seen.push(...materials)
      if (materials.length) {
        const activityType = String(child.activityType)
        if (!(activityType in grouped)) {
          grouped[activityType] = []
        }
        grouped[activityType].push(...materials)
      }
      return { grouped, seen: [...seen, ...materials] }
    },
    { grouped: {}, seen: [] },
  )

  const groups = Object.entries(grouped)
  if (!groups.length) return

  const noteVal = groups.reduce((acc, [key, list]) => {
    return (
      acc +
      wrapP(wrapSpan(wrapB(camelToTitle(key).toUpperCase()))) +
      createUnorderedList(list)
    )
  }, '')

  newNode.nodes.push(
    createNoteNode(
      { value: noteVal, title: 'Texts', studentFacing: false },
      context,
    ),
  )
}

function doLessonMaterials(
  newNode: SimplifiedNodeWithChildren,
  sourceNode: NormalizedIngestionSource,
  context: IngestionContext,
) {
  const material = sourceNode.material
  if (!Array.isArray(material)) return
  const sortedItems = material.reduce<Record<string, string[]>>(
    (acc, m) => {
      const type = m['ocx:learningResourceType'].toLowerCase()
      const nameString = String(m.name)

      if (type === 'reading guide') {
        acc.referenceGuides.push(nameString)
      } else if (type === 'question set') {
        acc.questionSets.push(nameString)
      } else {
        acc.tools.push(nameString)
      }
      return acc
    },
    {
      tools: [],
      referenceGuides: [],
      questionSets: [],
    },
  )

  const sortedKeys = ['tools', 'referenceGuides', 'questionSets']

  const noteContent = sortedKeys.reduce<string>((acc, key) => {
    const items = sortedItems[key]
    if (!items.length) return acc
    return (
      acc +
      wrapP(wrapSpan(wrapB(camelToTitle(key).toUpperCase()))) +
      createUnorderedList(sortedItems[key])
    )
  }, '')

  newNode.nodes.push(
    createNoteNode(
      { value: noteContent, title: 'Materials', studentFacing: false },
      context,
    ),
  )
}

function doKidActMap(
  newNode: SimplifiedNodeWithChildren,
  sourceNode: NormalizedIngestionSource,
  context: IngestionContext,
) {
  const materials = sourceNode['kid:actMat']
  if (!Array.isArray(materials)) return

  const materialsList = doAssociatedMaterials(materials)
  if (!materialsList.length) return

  newNode.nodes.push(
    createNoteNode(
      {
        value: createUnorderedList(materialsList),
        title: 'Texts',
        studentFacing: false,
      },
      context,
    ),
  )
}

function doKidProgramGuidance(
  newNode: SimplifiedNodeWithChildren,
  sourceNode: NormalizedIngestionSource,
  context: IngestionContext,
) {
  const { programGuide, remoteLearningGuide } = sourceNode[
    'kid:programGuidance'
  ] as any
  const nodeList = createGDriveUnitList(
    { listTitle: 'Program Guidance', unitTitle: 'Program Guidance' },
    programGuide,
    remoteLearningGuide,
  )
  if (nodeList) newNode.nodes.push(nodeList)
}

function doKidDrive(
  newNode: SimplifiedNodeWithChildren,
  sourceNode: NormalizedIngestionSource,
  context: IngestionContext,
) {
  const gDriveData = sourceNode['kid:gDrive']
  if (!gDriveData) return
  const { teacher, student, ...rest } = gDriveData as any
  const activityList = createGDriveActivityList(
    teacher,
    student,
    ...(Object.values(rest) as any),
  )
  if (!activityList) return
  if (activityList) newNode.nodes.push(activityList)
}

function createGDriveActivityList(
  ...items: ({ title: string; url?: string } | undefined)[]
) {
  const filtered = items.filter((i) => !!i)
  if (!filtered.length) return

  const nodes = filtered.reduce<ISimplifiedNode[]>((acc, item) => {
    if (!item) return acc
    const { title, url } = item
    if (!url) return acc
    return [
      ...acc,
      createActivity({ title }, [
        createAttachmentsList({
          title,
          attachments_list: [
            createAttachment({
              name: title,
              url,
            }),
          ],
        }),
      ]),
    ]
  }, [])
  return createNodeList({ title: 'Activities' }, nodes)
}

function createGDriveUnitList(
  { listTitle, unitTitle }: { listTitle: string; unitTitle: string },
  ...items: ({ title: string; url?: string } | undefined)[]
) {
  const activityList = createGDriveActivityList(...items)
  if (!activityList) return

  return createNodeList({ title: listTitle }, [
    createUnit(unitTitle, [activityList]),
  ])
}
